import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { PRIORIDADES, STAGES, roleLabel } from "@/lib/constants";
import { toast, confirmDialog } from "@/lib/toast";
import { Modal, Timeline, Empty, PrioBadge, StageBadge } from "@/components/ui-z";

const MENU_W = 260;
const MENU_H = 410; // altura com o item "Excluir chamado" (gestor)
const SUB_W = 160;

const HistoryModal = ({ t, onClose }) => {
  const { details, loadTicket } = useStore();
  const modal = useRef(null);
  const nav = useNavigate();
  const full = details[t.id];
  useEffect(() => { loadTicket(t.id); }, [t.id, loadTicket]);
  return (
    <Modal
      ref={modal} testId="history-modal" size="modal-lg" onClose={onClose}
      title={<><i className="fas fa-history mr-2" />Histórico &middot; {t.codigo}</>}
      footer={<><button type="button" className="btn btn-default" data-dismiss="modal">Fechar</button><button type="button" className="btn btn-primary" onClick={() => modal.current.hide(() => nav(`/chamados/${t.id}`))}>Abrir chamado</button></>}
    >
      {full === undefined ? <div className="text-center text-muted py-4"><i className="fas fa-2x fa-sync-alt fa-spin" /></div> : !full ? <Empty text="Não foi possível carregar o histórico." /> :
        full.historico.length ? <Timeline items={full.historico} /> : <Empty text="Sem registros no histórico." />}
    </Modal>
  );
};

const PriorityModal = ({ t, prioridade, onClose }) => {
  const { changePriority } = useStore();
  const modal = useRef(null);
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const label = PRIORIDADES.find((p) => p.key === prioridade)?.label;
  const save = async () => {
    setSaving(true);
    const ok = await changePriority(t.id, prioridade, nota || null);
    setSaving(false);
    if (ok) { toast.success(`Prioridade de ${t.codigo} alterada para ${label}`); modal.current.hide(); }
  };
  return (
    <Modal
      ref={modal} testId="priority-modal" onClose={onClose}
      title={<><i className="fas fa-flag mr-2" />Alterar prioridade &middot; {t.codigo}</>}
      footer={<><button type="button" className="btn btn-default" data-dismiss="modal">Cancelar</button><button type="button" data-testid="priority-confirm-btn" className="btn btn-primary" disabled={saving} onClick={save}>{saving && <i className="fas fa-spinner fa-spin mr-1" />}Confirmar</button></>}
    >
      <p>De <PrioBadge prio={t.prioridade} /> para <PrioBadge prio={prioridade} /></p>
      <div className="form-group mb-0">
        <label htmlFor="priority-note">Motivo (opcional)</label>
        <textarea id="priority-note" data-testid="priority-note-input" className="form-control" rows={3} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Registrado no histórico do chamado" />
      </div>
    </Modal>
  );
};

const TransferModal = ({ t, initialStage, onClose }) => {
  const { users, transferTicket } = useStore();
  const modal = useRef(null);
  const nextIdx = Math.min(STAGES.findIndex((s) => s.key === t.stage) + 1, 3);
  const [stage, setStage] = useState(initialStage || STAGES[nextIdx].key);
  const [assigned, setAssigned] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const ok = await transferTicket(t.id, { stage, assigned_to: assigned, nota });
    setSaving(false);
    if (ok) { toast.success(`Chamado ${t.codigo} transferido`); modal.current.hide(); }
  };
  return (
    <Modal
      ref={modal} testId="transfer-modal" onClose={onClose}
      title={<><i className="fas fa-exchange-alt mr-2" />Transferir nível &middot; {t.codigo}</>}
      footer={<><button type="button" className="btn btn-default" data-dismiss="modal">Cancelar</button><button type="button" data-testid="transfer-confirm-btn" className="btn btn-warning" disabled={saving} onClick={save}>{saving ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-exchange-alt mr-1" />}Transferir</button></>}
    >
      <p>Etapa atual: <StageBadge stage={t.stage} /></p>
      <div className="form-group">
        <label htmlFor="tm-stage">Encaminhar para</label>
        <select id="tm-stage" data-testid="transfer-modal-stage" className="form-control" value={stage} onChange={(e) => setStage(e.target.value)}>
          {STAGES.filter((s) => s.key !== "resolucao").map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="tm-assignee">Responsável</label>
        <select id="tm-assignee" data-testid="transfer-modal-assignee" className="form-control" value={assigned} onChange={(e) => setAssigned(e.target.value)}>
          <option value="">— Sem responsável —</option>
          {users.filter((u) => u.active).map((u) => <option key={u.id} value={u.id}>{u.name} ({roleLabel(u.role)})</option>)}
        </select>
      </div>
      <div className="form-group mb-0">
        <label htmlFor="tm-note">Nota</label>
        <textarea id="tm-note" data-testid="transfer-modal-note" rows={3} className="form-control" value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>
    </Modal>
  );
};

const ResolveModal = ({ t, onClose }) => {
  const { resolveTicket } = useStore();
  const modal = useRef(null);
  const [solucao, setSolucao] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const ok = await resolveTicket(t.id, solucao);
    setSaving(false);
    if (ok) { toast.success(`Chamado ${t.codigo} resolvido`); modal.current.hide(); }
  };
  return (
    <Modal
      ref={modal} testId="resolve-modal" onClose={onClose}
      title={<><i className="fas fa-check-circle mr-2" />Resolver &middot; {t.codigo}</>}
      footer={<><button type="button" className="btn btn-default" data-dismiss="modal">Cancelar</button><button type="button" data-testid="resolve-confirm-btn" className="btn btn-success" disabled={saving || !solucao.trim()} onClick={save}>{saving ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-check mr-1" />}Marcar como resolvido</button></>}
    >
      <div className="form-group mb-0">
        <label htmlFor="rm-solution">Solução aplicada</label>
        <textarea id="rm-solution" data-testid="resolve-modal-solution" rows={4} className="form-control" placeholder="Descreva a solução aplicada" value={solucao} onChange={(e) => setSolucao(e.target.value)} />
      </div>
    </Modal>
  );
};

// Exclusão de chamado: só gestor/admin (a API também confere). Pede confirmação com o código e devolve true se excluiu
export const canDeleteTickets = (user) => user?.role === "gestor" || user?.role === "admin";

export function useDeleteTicket() {
  const { user } = useAuth();
  const { deleteTicket } = useStore();
  const allowed = canDeleteTickets(user);
  const remove = useCallback(async (t) => {
    if (!allowed) return false;
    const ok = await confirmDialog({
      title: `Excluir o chamado ${t.codigo}?`,
      text: `"${t.titulo}" será apagado com histórico, comentários e evidências. Esta ação não pode ser desfeita.`,
      confirmText: "Sim, excluir",
      danger: true,
    });
    if (!ok || !(await deleteTicket(t.id))) return false;
    toast.success(`Chamado ${t.codigo} excluído`);
    return true;
  }, [allowed, deleteTicket]);
  return { allowed, remove };
}

// Menu de contexto de um chamado (botão direito; dropdown do Bootstrap aberto no ponto do clique) + modais de histórico e prioridade
export function useTicketActions() {
  const del = useDeleteTicket();
  const [menu, setMenu] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [priority, setPriority] = useState(null);
  const [transferOf, setTransferOf] = useState(null);
  const [resolveOf, setResolveOf] = useState(null);

  const open = useCallback((t, e) => {
    e.preventDefault();
    e.stopPropagation();
    const x = Math.max(8, Math.min(e.clientX, window.innerWidth - MENU_W - 8));
    const y = Math.max(8, Math.min(e.clientY, window.innerHeight - MENU_H - 8));
    setMenu({ t, x, y });
  }, []);
  const showHistory = useCallback((t, e) => { e?.stopPropagation(); setHistoryOf(t); }, []);
  const closeHistory = useCallback(() => setHistoryOf(null), []);
  const closePriority = useCallback(() => setPriority(null), []);
  const closeTransfer = useCallback(() => setTransferOf(null), []);
  const closeResolve = useCallback(() => setResolveOf(null), []);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("click", close);
    document.addEventListener("contextmenu", close);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("contextmenu", close);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu]);

  const t = menu?.t;
  const aberto = t?.status !== "resolvido";
  // Abre o submenu à esquerda quando não cabe à direita
  const subLeft = menu && menu.x + MENU_W + SUB_W > window.innerWidth;
  const element = (
    <>
      {menu && (
        <div className="dropdown-menu show shadow" data-testid="ticket-actions-menu" style={{ position: "fixed", top: menu.y, left: menu.x, minWidth: MENU_W, zIndex: 1060 }}>
          <Link to={`/chamados/${t.id}`} className="dropdown-item" data-testid="action-open"><i className="fas fa-eye mr-2" />Abrir chamado</Link>
          <button type="button" className="dropdown-item" data-testid="action-history" onClick={() => setHistoryOf(t)}><i className="fas fa-history mr-2" />Histórico</button>
          <div className="dropdown-divider" />
          <div className="dropdown-submenu dropdown-hover">
            <a href="#!" className={`dropdown-item dropdown-toggle ${aberto ? "" : "disabled"}`} data-testid="action-priority" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <i className="fas fa-flag mr-2" />Alterar prioridade
            </a>
            {aberto && (
              <div className="dropdown-menu shadow" style={subLeft ? { left: "auto", right: "100%" } : undefined}>
                {PRIORIDADES.map((p) => (
                  <button key={p.key} type="button" className="dropdown-item" data-testid={`action-priority-${p.key}`}
                    disabled={t.prioridade === p.key} onClick={() => setPriority({ t, prioridade: p.key })}>
                    <PrioBadge prio={p.key} />
                    {t.prioridade === p.key && <i className="fas fa-check float-right mt-1 text-success" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="dropdown-divider" />
          {aberto && (
            <>
              <button type="button" className="dropdown-item" data-testid="action-transfer" onClick={() => setTransferOf({ t })}><i className="fas fa-exchange-alt mr-2" />Transferir nível</button>
              <button type="button" className="dropdown-item" data-testid="action-resolve" onClick={() => setResolveOf(t)}><i className="fas fa-check-circle mr-2" />Resolver</button>
              <div className="dropdown-divider" />
            </>
          )}
          <button type="button" className="dropdown-item" data-testid="action-copy" onClick={() => { navigator.clipboard?.writeText(t.codigo); toast(`Código ${t.codigo} copiado`); }}>
            <i className="fas fa-copy mr-2" />Copiar código
          </button>
          <button type="button" className="dropdown-item" data-testid="action-copy-link" onClick={() => {
            const url = `${window.location.origin}/chamados/${t.id}`;
            navigator.clipboard?.writeText(url).then(() => toast(`Link do chamado ${t.codigo} copiado`), () => toast.error("Não foi possível copiar o link"));
          }}>
            <i className="fas fa-link mr-2" />Copiar link do chamado
          </button>
          {del.allowed && (
            <>
              <div className="dropdown-divider" />
              <button type="button" className="dropdown-item text-danger" data-testid="action-delete" onClick={() => del.remove(t)}>
                <i className="fas fa-trash mr-2" />Excluir chamado
              </button>
            </>
          )}
        </div>
      )}
      {historyOf && <HistoryModal t={historyOf} onClose={closeHistory} />}
      {transferOf && <TransferModal t={transferOf.t} initialStage={transferOf.stage} onClose={closeTransfer} />}
      {resolveOf && <ResolveModal t={resolveOf} onClose={closeResolve} />}
      {priority && <PriorityModal t={priority.t} prioridade={priority.prioridade} onClose={closePriority} />}
    </>
  );

  // stage opcional: etapa pré-selecionada (ex.: card solto numa coluna do Kanban)
  const showTransfer = useCallback((t, stage) => setTransferOf({ t, stage }), []);
  const showResolve = useCallback((t) => setResolveOf(t), []);
  return { open, showHistory, showTransfer, showResolve, element };
}
